import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

// ============================================================================
// NAV-01 — invoice detail + dispatch<->invoice linkage, from mirror_sales.
// Header-level only (invoice #, date, amount, labor/material split, dept,
// salesman, PO, linked dispatch). Full line-items / AR are MIR-11 (later,
// read-only slide-out per DEC-22).
// ============================================================================

export interface InvoiceRecord {
  invoice: string;
  dispatch: string | null;
  custno: string;
  locno: string | null;
  inv_date: string | null;
  ent_date: string | null;
  inv_type: string | null;
  inv_amount: number | null;
  labor_cost: number | null;
  mat_cost: number | null;
  other_cost: number | null;
  salesman: string | null;
  po_num: string | null;
  dept: string | null;
  quote_status: number | null;
  // Tender split from ESC (already mirrored): how the invoice was settled. The credit-card
  // AUTH number + card brand + check # live in a separate ESC tender table (not yet mirrored).
  amt_cash: number | null;
  amt_check: number | null;
  amt_credit_c: number | null;
  amt_charge: number | null;
  amt_applied_credits: number | null;
  // True paid status from ESC AR (dbo.Receivab → mirror_receivable) — authoritative, includes
  // payments applied AFTER invoicing. paid_off (timestamp) set = invoice fully settled.
  rec_inv_amt: number | null;
  rec_paid: number | null;
  rec_paid_off: string | null;
  /** ESC's point-in-time name snapshot — who the invoice was written for THEN.
   *  On a reused account this is the era-correct name (PROP-STORY-01: Keith's
   *  2024 invoice says "Keith Ehmke" here even though the account is Jane's now). */
  ship_name: string | null;
  customer_fullname?: string | null;
  salesman_name?: string | null;
}

const num = (v: unknown): number | null =>
  v === null || v === undefined || v === "" ? null : Number(v);

export function useInvoice(invoice: string | undefined) {
  return useQuery({
    queryKey: ["invoice", invoice],
    queryFn: async (): Promise<InvoiceRecord | null> => {
      if (!invoice) return null;
      const { data, error } = await supabase
        .from("mirror_sales")
        .select(
          "invoice, dispatch, custno, locno, inv_date, ent_date, inv_type, inv_amount, labor_cost, mat_cost, other_cost, salesman, po_num, dept, quote_status, amt_cash, amt_check, amt_credit_c, amt_charge, amt_applied_credits, ship_name",
        )
        .eq("invoice", invoice)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const r = data as Record<string, unknown>;
      const rec: InvoiceRecord = {
        invoice: String(r.invoice),
        dispatch: (r.dispatch as string) ?? null,
        custno: String(r.custno),
        locno: (r.locno as string) ?? null,
        inv_date: (r.inv_date as string) ?? null,
        ent_date: (r.ent_date as string) ?? null,
        inv_type: (r.inv_type as string) ?? null,
        inv_amount: num(r.inv_amount),
        labor_cost: num(r.labor_cost),
        mat_cost: num(r.mat_cost),
        other_cost: num(r.other_cost),
        salesman: (r.salesman as string) ?? null,
        po_num: (r.po_num as string) ?? null,
        dept: (r.dept as string) ?? null,
        quote_status: num(r.quote_status),
        amt_cash: num(r.amt_cash),
        amt_check: num(r.amt_check),
        amt_credit_c: num(r.amt_credit_c),
        amt_charge: num(r.amt_charge),
        amt_applied_credits: num(r.amt_applied_credits),
        ship_name: (r.ship_name as string) ?? null,
        // Filled from mirror_receivable just below (kept here so the literal
        // satisfies InvoiceRecord).
        rec_inv_amt: null,
        rec_paid: null,
        rec_paid_off: null,
      };
      const [{ data: cust }, { data: emp }, { data: recvRows }] = await Promise.all([
        supabase.from("mirror_customer").select("fullname").eq("custno", rec.custno).maybeSingle(),
        rec.salesman
          ? supabase.from("mirror_employee").select("empname").eq("empno", rec.salesman).maybeSingle()
          : Promise.resolve({ data: null } as { data: { empname?: string } | null }),
        // AR row for this invoice (rec_type 'I' = the invoice itself; finance-charge rows excluded).
        supabase.from("mirror_receivable").select("inv_amt, paid, paid_off").eq("invoice", rec.invoice).eq("rec_type", "I").limit(1),
      ]);
      rec.customer_fullname = (cust as { fullname?: string } | null)?.fullname ?? null;
      rec.salesman_name = (emp as { empname?: string } | null)?.empname ?? null;
      const recv = (recvRows as { inv_amt?: number; paid?: number; paid_off?: string }[] | null)?.[0] ?? null;
      rec.rec_inv_amt = num(recv?.inv_amt);
      rec.rec_paid = num(recv?.paid);
      rec.rec_paid_off = recv?.paid_off ?? null;
      return rec;
    },
    enabled: !!invoice,
    staleTime: 60 * 1000,
  });
}

// --- MIR-11: invoice line items + per-tech rows ----------------------------

export interface InvoiceLine {
  invoice: string;
  line_count: string;
  prod: string | null;
  line_desc: string | null;
  quan: number | null;
  price: number | null;
  amount: number | null;
  cost: number | null;
  ptype: string | null;
  no_print: boolean;
}

export function useInvoiceLines(invoice: string | undefined) {
  return useQuery({
    queryKey: ["invoice_lines", invoice],
    enabled: !!invoice,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<InvoiceLine[]> => {
      const { data, error } = await supabase
        .from("mirror_sales_line")
        .select(
          "invoice, line_count, prod, line_desc, quan, price, amount, cost, ptype, no_print",
        )
        .eq("invoice", invoice!)
        .order("line_count", { ascending: true });
      if (error) throw error;
      return (data ?? []) as InvoiceLine[];
    },
  });
}

export interface InvoiceTechRow {
  invoice: string;
  line_count: string;
  empno: string | null;
  credit_pct: number | null;
  reg_hr: number | null;
  ot_hr: number | null;
  lab_amt: number | null;
  overhead: number | null;
  dispatch: string | null;
  /** Resolved from mirror_employee. */
  name?: string;
}

/** The "actual hours and techs on the invoice" (Seth 2026-06-11) — ESC's
 *  SalesEmp rows: who worked it, reg/OT hours, labor cost, % credit. */
export function useInvoiceTechs(invoice: string | undefined) {
  return useQuery({
    queryKey: ["invoice_techs", invoice],
    enabled: !!invoice,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<InvoiceTechRow[]> => {
      const { data, error } = await supabase
        .from("mirror_sales_emp")
        .select("*")
        .eq("invoice", invoice!)
        .order("line_count", { ascending: true });
      if (error) throw error;
      const rows = (data ?? []) as InvoiceTechRow[];
      const empnos = [...new Set(rows.map((r) => r.empno).filter(Boolean))] as string[];
      if (empnos.length > 0) {
        const { data: emps } = await supabase
          .from("mirror_employee")
          .select("empno, empname")
          .in("empno", empnos);
        const names = new Map(
          ((emps ?? []) as { empno: string; empname: string | null }[]).map((e) => [
            e.empno,
            e.empname ?? e.empno,
          ]),
        );
        for (const r of rows) {
          if (r.empno) r.name = names.get(r.empno) ?? r.empno;
        }
      }
      return rows;
    },
  });
}

export interface DispatchSale {
  invoice: string;
  inv_date: string | null;
  inv_amount: number | null;
  inv_type: string | null;
}

export interface CustomerInvoiceRow {
  invoice: string;
  dispatch: string | null;
  inv_date: string | null;
  inv_type: string | null;
  dept: string | null;
  inv_amount: number | null;
  /** AR-360: which property the invoice billed — the Verde-style
   *  "which address is this invoice for" read on multi-property accounts. */
  locno: string | null;
}

/** All of a customer's invoices, header-level, read-only from ESC `mirror_sales`,
 *  newest first — drives the Customer 360 Invoices tab. Customer-wide (billing
 *  is a customer-level concern, not per-location). Line-items + AR balance are
 *  MIR-11 / the AR read path (later, per DEC-22). */
export function useCustomerInvoices(custno: string | undefined) {
  return useQuery({
    queryKey: ["customer_invoices", custno],
    enabled: !!custno,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<CustomerInvoiceRow[]> => {
      if (!custno) return [];
      const { data, error } = await supabase
        .from("mirror_sales")
        .select("invoice, dispatch, inv_date, inv_type, dept, inv_amount, locno")
        .eq("custno", custno)
        .order("inv_date", { ascending: false })
        .limit(500);
      if (error) throw error;
      return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
        invoice: String(r.invoice),
        dispatch: (r.dispatch as string) ?? null,
        inv_date: (r.inv_date as string) ?? null,
        inv_type: (r.inv_type as string) ?? null,
        dept: (r.dept as string) ?? null,
        inv_amount: num(r.inv_amount),
        locno: (r.locno as string) ?? null,
      }));
    },
  });
}

/** Sales/invoices for a dispatch (0 / 1 / many) — drives the dispatch->invoice link. */
export function useSalesByDispatch(dispatch: string | undefined) {
  return useQuery({
    queryKey: ["sales_by_dispatch", dispatch],
    queryFn: async (): Promise<DispatchSale[]> => {
      if (!dispatch) return [];
      const { data, error } = await supabase
        .from("mirror_sales")
        .select("invoice, inv_date, inv_amount, inv_type")
        .eq("dispatch", dispatch)
        .order("inv_date", { ascending: false });
      if (error) throw error;
      return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
        invoice: String(r.invoice),
        inv_date: (r.inv_date as string) ?? null,
        inv_amount: num(r.inv_amount),
        inv_type: (r.inv_type as string) ?? null,
      }));
    },
    enabled: !!dispatch,
    staleTime: 60 * 1000,
  });
}
